import { useMutation } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useCallback } from "react";

import { NotificationsLevel } from "../../../lib/enums";
import { ErrorResponse } from "../../../requests/@types";
import { route } from "../../../shim/jsbridge";
import { show } from "../../../notifications/notifications";
import api from "../api";
import { HTTP_TOO_MANY_REQUEST_STATUS_CODE } from "../consts";
import { sampleReportKeys } from "../queryKeys";

/**
 * Downloads dropped files in 7z format
 * @param hash samples hash value
 * @param analysisId analysisId for specific analysis
 * @return { downloadDroppedFiles }
 */
const useDownloadDroppedFiles = (
  hash: string,
  analysisId: string,
  isNetwork = false
) => {
  const downloadUrl = route(
    "api_rl_dynamic_analysis_resource_dropped_files",
    [hash, analysisId],
    isNetwork ? { isNetwork } : {}
  );

  const { mutate, isPending } = useMutation<AxiosResponse<void>, ErrorResponse>(
    {
      mutationKey: sampleReportKeys.downloadDroppedFiles(
        hash,
        analysisId,
        isNetwork
      ),
      mutationFn: () =>
        api.downloadRlSandboxReportDroppedFiles(hash, analysisId, isNetwork),
      onSuccess: () => {
        window.location.assign(downloadUrl);
      },
      onError: responseError => {
        const errorMessage =
          responseError.status === HTTP_TOO_MANY_REQUEST_STATUS_CODE
            ? "Exceeding disk or memory usage, check system health status for more information."
            : "Unexpected error occurred while downloading dropped files.";

        show({
          level: NotificationsLevel.ERROR,
          message: errorMessage
        });
      }
    }
  );

  const downloadDroppedFiles = useCallback(() => {
    show({
      level: NotificationsLevel.SUCCESS,
      message: "Dropped files download has started..."
    });
    mutate();
  }, [mutate]);

  return { downloadDroppedFiles, isLoading: isPending };
};

export default useDownloadDroppedFiles;
